import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { todayIsoDate, useAttendanceOrgTimeZone } from '@/pages/attendance/data'
import { WorkerProfileLink } from '@/pages/attendance/worker-link'
import { DEFAULT_OFFICE_HOURS, officeHoursApi } from '@/services/office-hours'
import { workersApi, type AttendanceReportWorker } from '@/services/workers'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const WEEKLY_OFF_KEY = 'attendance_report_weekly_off'

const DAY_STYLE: Record<string, { label: string; className: string }> = {
  P: { label: 'Present', className: 'bg-emerald-500 text-white' },
  L: { label: 'Leave', className: 'bg-sky-400 text-white' },
  A: { label: 'Absent', className: 'bg-red-400 text-white' },
  O: { label: 'Weekly off', className: 'bg-slate-200 text-slate-500' },
  H: { label: 'Holiday', className: 'bg-violet-200 text-violet-700' },
  N: { label: 'Not joined yet', className: 'bg-slate-50 text-slate-300' },
  '-': { label: 'Upcoming', className: 'bg-white text-slate-300 ring-1 ring-inset ring-slate-200' },
}

function storedWeeklyOff(): number[] | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(WEEKLY_OFF_KEY) || 'null')
    if (Array.isArray(parsed)) return parsed.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6)
  } catch {
    // Ignore a corrupt saved value.
  }
  return null
}

function hoursLabel(minutes: number) {
  if (!minutes) return '0h'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/** Days the worker was expected in: present on a working day, on leave, or absent. */
function expectedDays(row: AttendanceReportWorker) {
  return row.present_days - row.present_on_off_days + row.leave_days + row.absent_days
}

function attendanceRate(row: AttendanceReportWorker) {
  const expected = expectedDays(row)
  return expected ? Math.round(((row.present_days - row.present_on_off_days) / expected) * 100) : null
}

export default function AttendanceReportsPage() {
  const today = todayIsoDate(useAttendanceOrgTimeZone())
  const [pickedMonth, setMonth] = useState<string | null>(null)
  const month = pickedMonth ?? today.slice(0, 7)
  const [pickedWeeklyOff, setWeeklyOff] = useState<number[] | null>(storedWeeklyOff)
  const officeHoursQuery = useQuery({ queryKey: ['office-hours'], queryFn: officeHoursApi.get })
  const closedDays = (officeHoursQuery.data ?? DEFAULT_OFFICE_HOURS).days.filter((day) => !day.open).map((day) => day.day - 1)
  const weeklyOff = pickedWeeklyOff ?? closedDays
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<number | null>(null)

  const weeklyOffParam = [...weeklyOff].sort().join(',')
  const reportQuery = useQuery({
    queryKey: ['attendance-monthly-report', month, weeklyOffParam],
    queryFn: () => workersApi.getAttendanceMonthlyReport({ month, weekly_off: weeklyOffParam }),
    enabled: pickedWeeklyOff != null || !officeHoursQuery.isLoading,
    placeholderData: (previous) => previous,
  })
  const report = reportQuery.data

  const toggleDay = (day: number) => {
    const next = weeklyOff.includes(day) ? weeklyOff.filter((value) => value !== day) : [...weeklyOff, day]
    setWeeklyOff(next)
    localStorage.setItem(WEEKLY_OFF_KEY, JSON.stringify(next))
  }

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (report?.workers ?? []).filter((row) => !term || `${row.name} ${row.worker_code || ''}`.toLowerCase().includes(term))
  }, [report, search])

  const totals = useMemo(() => rows.reduce(
    (sum, row) => ({
      present: sum.present + row.present_days,
      leave: sum.leave + row.leave_days,
      absent: sum.absent + row.absent_days,
      minutes: sum.minutes + row.minutes,
    }),
    { present: 0, leave: 0, absent: 0, minutes: 0 },
  ), [rows])

  const dayNumbers = useMemo(() => {
    if (!report) return []
    const [year, monthIndex] = report.month.split('-').map(Number)
    return Array.from({ length: report.workers[0]?.days.length ?? new Date(year, monthIndex, 0).getDate() }, (_, index) => {
      const weekday = (new Date(Date.UTC(year, monthIndex - 1, index + 1)).getUTCDay() + 6) % 7
      return { day: index + 1, weekday }
    })
  }, [report])

  const exportCsv = () => {
    if (!report) return
    const header = ['Employee', 'Code', 'Expected days', 'Present', 'Present on off days', 'Leave', 'Absent', 'Hours', 'Attendance %', ...dayNumbers.map(({ day }) => String(day))]
    const body = rows.map((row) => [
      row.name,
      row.worker_code || '',
      expectedDays(row),
      row.present_days,
      row.present_on_off_days,
      row.leave_days,
      row.absent_days,
      (row.minutes / 60).toFixed(2),
      attendanceRate(row) ?? '',
      ...row.days.split(''),
    ])
    const csv = [header, ...body].map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `attendance-report-${report.month}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 pb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Attendance Report</h1>
          <p className="mt-1 text-sm text-slate-500">Days present, leave, absences and hours logged by each employee for a month.</p>
        </div>
        <Button type="button" variant="outline" onClick={exportCsv} disabled={!report || rows.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      <section className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Month
          <Input
            type="month"
            className="w-[170px]"
            value={month}
            max={today.slice(0, 7)}
            onChange={(event) => { setMonth(event.target.value || null); setExpanded(null) }}
          />
        </label>
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          Weekly off
          <div className="flex gap-1" role="group" aria-label="Weekly off days">
            {WEEKDAYS.map((label, day) => {
              const off = weeklyOff.includes(day)
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={off}
                  onClick={() => toggleDay(day)}
                  className={cn(
                    'h-8 w-11 rounded-md border text-xs font-medium transition-colors',
                    off ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
        <Input className="ml-auto max-w-xs" placeholder="Search employee or ID..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </section>

      {report ? (
        <div className="grid gap-3 md:grid-cols-4">
          <Stat
            label="Working days"
            value={String(report.working_days)}
            detail={report.elapsed_working_days < report.working_days ? `${report.elapsed_working_days} so far this month` : 'Excluding weekly offs and holidays'}
          />
          <Stat label="Days present" value={String(totals.present)} detail={`Across ${rows.length} employee${rows.length === 1 ? '' : 's'}`} />
          <Stat label="Leave / absent" value={`${totals.leave} / ${totals.absent}`} detail="Approved leave days / days missed" />
          <Stat label="Hours logged" value={hoursLabel(totals.minutes)} detail="From completed punches" />
        </div>
      ) : null}

      {report && report.holidays.length > 0 ? (
        <p className="text-xs text-slate-500">
          Holidays: {report.holidays.map((holiday) => `${Number(holiday.date.slice(8))} – ${holiday.title}`).join(', ')}
        </p>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {reportQuery.isPending ? (
          <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-12 w-full" />)}</div>
        ) : reportQuery.error ? (
          <p className="p-6 text-sm text-destructive">{getErrorMessage(reportQuery.error)}</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">No employees to report for this month.</p>
        ) : (
          <div className={cn('overflow-x-auto', reportQuery.isFetching && 'opacity-60')}>
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {['Employee', 'Expected', 'Present', 'Leave', 'Absent', 'Hours logged', 'Avg / present day', 'Attendance'].map((heading) => (
                    <th key={heading} className="px-4 py-3 font-medium">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rate = attendanceRate(row)
                  const open = expanded === row.worker_id
                  return (
                    <Fragment key={row.worker_id}>
                      <tr
                        className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                        onClick={() => setExpanded(open ? null : row.worker_id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {open ? <ChevronDown className="mt-0.5 h-4 w-4 text-slate-400" /> : <ChevronRight className="mt-0.5 h-4 w-4 text-slate-400" />}
                            <div>
                              <WorkerProfileLink workerId={row.worker_id} name={row.name} className="font-medium text-slate-900" />
                              <div className="text-xs text-slate-500">{row.worker_code || row.worker_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 tabular-nums">{expectedDays(row)}</td>
                        <td className="px-4 py-3 tabular-nums">
                          <span className="font-medium text-emerald-700">{row.present_days}</span>
                          {row.present_on_off_days ? (
                            <div className="text-[11px] text-slate-500">incl. {row.present_on_off_days} on off days</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-sky-700">{row.leave_days}</td>
                        <td className={cn('px-4 py-3 tabular-nums', row.absent_days ? 'font-medium text-red-600' : 'text-slate-500')}>{row.absent_days}</td>
                        <td className="px-4 py-3 tabular-nums font-medium">
                          {hoursLabel(row.minutes)}
                          {row.open_punches ? <div className="text-[11px] font-normal text-amber-600">{row.open_punches} open punch{row.open_punches === 1 ? '' : 'es'} not counted</div> : null}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-slate-600">{row.present_days ? hoursLabel(Math.round(row.minutes / row.present_days)) : '—'}</td>
                        <td className="px-4 py-3">
                          {rate == null ? '—' : (
                            <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', rate >= 90 ? 'bg-emerald-50 text-emerald-700' : rate >= 75 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700')}>
                              {rate}%
                            </span>
                          )}
                        </td>
                      </tr>
                      {open ? (
                        <tr className="bg-slate-50/60">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {row.days.split('').map((mark, index) => {
                                const style = DAY_STYLE[mark] ?? DAY_STYLE['-']
                                const info = dayNumbers[index]
                                return (
                                  <div
                                    key={index}
                                    title={`${WEEKDAYS[info?.weekday ?? 0]} ${report?.month}-${String(info?.day).padStart(2, '0')}: ${style.label}`}
                                    className={cn('flex h-10 w-8 flex-col items-center justify-center rounded text-[10px] leading-tight', style.className)}
                                  >
                                    <span className="font-semibold">{info?.day}</span>
                                    <span>{WEEKDAYS[info?.weekday ?? 0].slice(0, 2)}</span>
                                  </div>
                                )
                              })}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                              {Object.entries(DAY_STYLE).map(([mark, style]) => (
                                <span key={mark} className="flex items-center gap-1">
                                  <span className={cn('inline-block h-3 w-3 rounded-sm', style.className)} />
                                  {style.label}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{detail}</div>
    </div>
  )
}
